import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  RefObject,
} from 'react';

import { useLazyQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { AuthContext, AuthContextInterface } from '../context/AuthProvider/AuthContext';
import useScriptLoader, { SCRIPTS } from '../context/ScriptLoader/useScriptLoader';
import {
  GET_B2B_LEARNER_SUBSCRIPTION_INFORMATION,
  GET_B2C_LEARNER_SUBSCRIPTION_INFORMATION,
  GET_COMPANY_OWNER_SUBSCRIPTION_INFORMATION,
  GET_LEARNER_INFO,
  GET_MANAGER_SUBSCRIPTION_INFORMATION,
} from '../graphql/user';
import { HITS_TYPES_BY_URL } from '../pages/DiscoverPage/const';
import { USER_ROLES } from '../pages/const';
import {
  MySubscriptionB2BLearnerChargebeeInfo,
  MySubscriptionB2CLearnerChargebeeInfo,
  MySubscriptionCompanyOwnerChargebeeInfo,
  MySubscriptionTeamManagerChargebeeInfo,
  Query,
} from '../types';
import useLocalStorage from './hooks/useLocalStorage';

export const useEventListener = (eventName, handler, element = window, options?) => {
  // Create a ref that stores handler
  const savedHandler = useRef();
  savedHandler.current = handler;

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On
      const isSupported = element && element.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref
      // @ts-ignore
      const eventListener = (event) => savedHandler.current(event);

      // Add event listener
      element.addEventListener(eventName, eventListener, options);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener, options);
      };
    },
    [eventName, element, options, savedHandler], // Re-run if eventName or element changes
  );
};

export const isFormulaRegExp = /(\\\[.*\\])|(\\\(.*\\\))|(\$\$.*\$\$)|(<formula>.*<\\formula>)/g;

export const useMathJaxScriptLoader = (skip = false) => {
  const nodeRef = useRef(null);
  const isLoaded = useScriptLoader(SCRIPTS.MATH_JAX, skip);

  useEffect(() => {
    //check if script url exists and script not loaded yet
    if (!isLoaded) {
      //mathjax config
      //@ts-ignore
      window.MathJax = {
        tex: {
          inlineMath: [['\\(', '\\)']],
          displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]'],
            ['<formula>', '</formula>'],
          ],
        },
      };
    }
  }, [isLoaded]);

  useEffect(() => {
    // @ts-ignore:
    window?.MathJax?.typesetPromise?.([nodeRef.current]);
  }, [isLoaded, nodeRef]);

  return nodeRef;
};

export const useSERPNavigation = () => {
  const location = useLocation();
  const [type, setType] = useState('');

  useEffect(() => {
    const typeRegExp = new RegExp('[^/]+/?$', 'g');
    const match = location.pathname.match(typeRegExp);
    if (match && match[0]) {
      setType(match[0].replace(/\/$/, ''));
    }
  }, [location]);

  return HITS_TYPES_BY_URL[type];
};

export const useAuth = (): AuthContextInterface => useContext(AuthContext);

//hook used to refresh user info and token based on subscription change
export const useUpdateUserInfoAndToken = (callback?: () => void) => {
  const [loadUserInfo] = useLazyQuery<Pick<Query, 'getLearnerInfo'>>(GET_LEARNER_INFO, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    onCompleted: () => {
      callback && callback();
    },
  });

  const updateUserInfo = useCallback(async () => {
    await loadUserInfo();
  }, [loadUserInfo]);

  return {
    updateUserInfo,
  };
};

export type UseTablePaginationType = {
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const useTablePagination = ({
  defaultRowsPerPage = 50,
  defaultPage = 0,
  perPageKey,
}): UseTablePaginationType => {
  const [page, setPage] = useState(defaultPage);
  const { value: rowsPerPage, updateLocStorageValue } = useLocalStorage(
    perPageKey,
    defaultRowsPerPage,
  );

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateLocStorageValue(parseInt(event.target.value, 10));
      setPage(0);
    },
    [updateLocStorageValue, setPage],
  );

  return {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

export const useIsIE = () => {
  const [isIE, setIsIE] = useState(false);

  useEffect(() => {
    //@ts-ignore
    const isIE = /*@cc_on!@*/ false || !!document?.documentMode;

    setIsIE(isIE);
  }, [setIsIE]);

  return isIE;
};

export const usePrevious = (value) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

type UseSuscriptionInfoType = {
  loadSubInfo: (role: USER_ROLES) => void;
  subInfo?:
    | MySubscriptionCompanyOwnerChargebeeInfo
    | MySubscriptionTeamManagerChargebeeInfo
    | MySubscriptionB2BLearnerChargebeeInfo
    | MySubscriptionB2CLearnerChargebeeInfo
    | null;
};

export const useSubscriptionInfo = (): UseSuscriptionInfoType => {
  const [loadCompanyOwnerSubData, { data: companyOwnerSubData }] = useLazyQuery<
    Pick<Query, 'getMySubscriptionCompanyOwnerChargebeeInfo'>
  >(GET_COMPANY_OWNER_SUBSCRIPTION_INFORMATION);

  const [loadManagerSubData, { data: managerSubData }] = useLazyQuery<
    Pick<Query, 'getMySubscriptionTeamManagerChargebeeInfo'>
  >(GET_MANAGER_SUBSCRIPTION_INFORMATION);

  const [loadB2BLeanerSubData, { data: b2bLearnerSubData }] = useLazyQuery<
    Pick<Query, 'getMySubscriptionB2BLearnerChargebeeInfo'>
  >(GET_B2B_LEARNER_SUBSCRIPTION_INFORMATION);

  const [loadB2CLearnerSubData, { data: b2cLearnerSubData }] = useLazyQuery<
    Pick<Query, 'getMySubscriptionB2CLearnerChargebeeInfo'>
  >(GET_B2C_LEARNER_SUBSCRIPTION_INFORMATION);

  const subInfo = useMemo(() => {
    return (
      companyOwnerSubData?.getMySubscriptionCompanyOwnerChargebeeInfo ||
      managerSubData?.getMySubscriptionTeamManagerChargebeeInfo ||
      b2bLearnerSubData?.getMySubscriptionB2BLearnerChargebeeInfo ||
      b2cLearnerSubData?.getMySubscriptionB2CLearnerChargebeeInfo
    );
  }, [companyOwnerSubData, managerSubData, b2bLearnerSubData, b2cLearnerSubData]);

  const loadSubInfo = useCallback(
    (role: USER_ROLES) => {
      switch (role) {
        case USER_ROLES.CompanyOwner: {
          loadCompanyOwnerSubData();
          break;
        }
        case USER_ROLES.TeamManager: {
          loadManagerSubData();
          break;
        }
        case USER_ROLES.B2BLearner: {
          loadB2BLeanerSubData();
          break;
        }
        case USER_ROLES.B2CLearner: {
          loadB2CLearnerSubData();
          break;
        }
        default: {
          return;
        }
      }
    },
    [loadB2CLearnerSubData, loadB2BLeanerSubData, loadCompanyOwnerSubData, loadManagerSubData],
  );

  return {
    loadSubInfo,
    subInfo,
  };
};

type UseModal = {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModal(callback?: () => void);
  handleCloseModal(callback?: () => void);
};

export const useModal = (): UseModal => {
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = useCallback((callback?: () => void) => {
    setOpenModal(true);
    callback && callback();
  }, []);

  const handleCloseModal = useCallback((callback?: () => void) => {
    setOpenModal(false);
    callback && callback();
  }, []);

  return {
    openModal,
    setOpenModal,
    handleOpenModal,
    handleCloseModal,
  };
};

export function useTooltipOffset() {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (wrapperRef?.current) {
      const centerX = wrapperRef.current.clientWidth / 2;
      const height = wrapperRef.current.clientHeight;
      const clientX = event.clientX;
      const clientY = event.clientY;
      const elX = wrapperRef.current.getBoundingClientRect().x;
      const elY = wrapperRef.current.getBoundingClientRect().y;
      const mouseXPosOnEl = clientX - elX;
      const mouseYPosOnEl = clientY - elY;

      setOffset({ x: mouseXPosOnEl - centerX, y: mouseYPosOnEl - height });
    }
  };

  return [offset, wrapperRef, handleMouseEnter] as [
    { x: number; y: number },
    RefObject<HTMLDivElement>,
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
  ];
}
